"use client";

import { useEffect, useState } from "react";
import Preloader from "./preloader";
import BootstrapError from "./bootstrap-error";
import {
  useBootConnectionError,
  useBootProgress,
  useBootReady,
} from "@/lib/boot-state";
import { cn } from "@/lib/utils";

/** One full-window splash stays mounted while the protected app boots beneath it.
 * Readiness is sticky: fade once, then leave the application shell in place.
 */
export default function BootOverlay() {
  const ready = useBootReady();
  const connectionError = useBootConnectionError();
  const progress = useBootProgress();
  const [timedOut, setTimedOut] = useState(false);

  // Measure inactivity, not total boot duration. Each new session/socket/view
  // milestone grants a fresh window, and late progress dismisses the stall UI.
  useEffect(() => {
    setTimedOut(false);
    if (ready) return;
    const timer = setTimeout(() => setTimedOut(true), 30_000);
    return () => clearTimeout(timer);
  }, [ready, progress]);
  const [gone, setGone] = useState(false);

  // Unmount fallback for environments where transitionend never fires
  // (prefers-reduced-motion disables the transition; jsdom has no CSS engine).
  useEffect(() => {
    if (!ready || gone) return;
    const timer = setTimeout(() => setGone(true), 600);
    return () => clearTimeout(timer);
  }, [ready, gone]);

  if (gone) return null;

  return (
    <div
      data-testid="boot-overlay"
      className={cn(
        "fixed inset-0 z-[100] bg-background transition-opacity duration-300 ease-in-out motion-reduce:transition-none",
        ready && "pointer-events-none opacity-0",
      )}
      onTransitionEnd={(event) => {
        if (event.target === event.currentTarget && ready) setGone(true);
      }}
    >
      {!ready && (connectionError || timedOut) ? (
        <BootstrapError
          error={connectionError}
          onRetry={() => window.location.reload()}
        />
      ) : (
        <div role="status" aria-label="Loading Portal">
          <Preloader />
        </div>
      )}
    </div>
  );
}
